/**
 * Shared helpers for room-object thumbnails (Model Library, Model Studio curated list).
 */

export function pickRoomObjectImageUrl(obj) {
  if (!obj || typeof obj !== 'object') return '';
  const nested = obj.image && typeof obj.image === 'object' ? obj.image : null;
  const candidates = [
    obj.aiResultImageS3Key,
    obj.ai_result_image_s3_key,
    obj.imageS3Key,
    obj.image_s3_key,
    obj.imageUrl,
    obj.image_url,
    obj.imageURL,
    obj.url,
    obj.imageKey,
    nested?.aiResultImageS3Key,
    nested?.ai_result_image_s3_key,
    nested?.imageS3Key,
    nested?.image_s3_key,
    nested?.url,
    nested?.imageUrl,
    nested?.s3Key,
    typeof obj.image === 'string' ? obj.image : null,
    obj.s3Key,
    obj.s3_key,
  ];
  const raw = candidates.find((v) => typeof v === 'string' && v.trim().length > 0);
  if (!raw) return '';
  const trimmed = raw.trim();
  if (trimmed.startsWith('s3://')) {
    const withoutScheme = trimmed.slice('s3://'.length);
    const slash = withoutScheme.indexOf('/');
    return slash >= 0 ? withoutScheme.slice(slash + 1) : withoutScheme;
  }
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  return trimmed;
}

/**
 * Vite dev server enables COEP `require-corp`, which blocks embedding typical S3 responses.
 * Rewrite known bucket URLs to same-origin proxy (see `vite.config.js`).
 */
export function parseRoomObjectIdFromApiResponse(res) {
  const raw =
    res?.id ?? res?.objectId ?? res?.object_id ?? res?.roomObjectId ?? res?.room_object_id;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

const ROOM_OBJECT_UPLOAD_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png']);

function loadImageFromUrl(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to decode image for upload.'));
    img.src = url;
  });
}

function imageToPngFile(img, baseName) {
  const w = Number(img.naturalWidth) || Number(img.width) || 0;
  const h = Number(img.naturalHeight) || Number(img.height) || 0;
  if (!(w > 0) || !(h > 0)) {
    return Promise.reject(new Error('Image has no dimensions.'));
  }
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return Promise.reject(new Error('Could not prepare image for upload.'));
  }
  ctx.drawImage(img, 0, 0);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to encode image as PNG.'));
          return;
        }
        const safe = String(baseName).replace(/[^\w\- ]+/g, '').trim() || 'model';
        resolve(new File([blob], `${safe}.png`, { type: 'image/png' }));
      },
      'image/png',
    );
  });
}

/**
 * Download a display URL and return a PNG/JPEG File accepted by POST /rooms/{id}/objects.
 * Re-encodes via canvas when the response MIME type is missing or unsupported.
 */
export async function fetchUrlAsImageFile(url, baseName = 'model') {
  const displayUrl = resolveRoomObjectImageSrcForDisplay(String(url).trim());
  if (!displayUrl) {
    throw new Error('Image URL is missing.');
  }

  const res = await fetch(displayUrl);
  if (!res.ok) {
    throw new Error(`Failed to download image (${res.status})`);
  }
  const blob = await res.blob();
  const type = (blob.type || '').toLowerCase();
  if (ROOM_OBJECT_UPLOAD_TYPES.has(type)) {
    const ext = type.includes('jpeg') || type.includes('jpg') ? 'jpg' : 'png';
    const safe = String(baseName).replace(/[^\w\- ]+/g, '').trim() || 'model';
    return new File([blob], `${safe}.${ext}`, { type });
  }

  const objectUrl = URL.createObjectURL(blob);
  try {
    const img = await loadImageFromUrl(objectUrl);
    return await imageToPngFile(img, baseName);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/** Decode URL to a validated PNG File (always safe for room object upload). */
export async function fetchUrlAsPngFileForRoomObject(url, baseName = 'model') {
  const displayUrl = resolveRoomObjectImageSrcForDisplay(String(url).trim());
  if (!displayUrl) {
    throw new Error('Image URL is missing.');
  }
  const img = await loadImageFromUrl(displayUrl);
  return imageToPngFile(img, baseName);
}

export function resolveRoomObjectImageSrcForDisplay(url) {
  if (!url) return '';
  const trimmed = String(url).trim();
  if (!trimmed) return '';
  if (!/^https?:\/\//i.test(trimmed) && !trimmed.startsWith('//')) {
    return trimmed;
  }
  try {
    if (import.meta.env.DEV) {
      const parsed = new URL(trimmed.startsWith('//') ? `https:${trimmed}` : trimmed);
      if (parsed.hostname === 'dxliving-3d.s3.amazonaws.com') {
        return `/s3-dxliving${parsed.pathname}${parsed.search}`;
      }
      if (
        parsed.hostname === 's3.amazonaws.com' &&
        parsed.pathname.startsWith('/dxliving-3d/')
      ) {
        return `/s3-dxliving${parsed.pathname.replace(/^\/dxliving-3d/, '')}${parsed.search}`;
      }
    }
  } catch {
    return trimmed;
  }
  return trimmed;
}
